import React, { useRef, useEffect, useState } from "react";
import { useDrag } from "react-dnd";
import { motion } from "framer-motion";
import {
  Type,
  Image as ImageIcon,
  Square,
  Circle,
  Triangle,
  Hexagon,
  Star,
  PanelRightClose,
  ToggleLeft,
  Radio,
  CheckSquare,
  Edit,
  MousePointerClick,
  SlidersHorizontal,
  Target,
  ListOrdered,
  SplitSquareHorizontal,
  Shapes,
  PencilRuler,
  Spline,
  X,
} from "lucide-react";

// Core item definitions
const textItem = {
  type: "text",
  icon: Type,
  label: "Text",
  dimensions: { width: 256, height: 64 },
};

const imageItem = {
  type: "image_upload",
  icon: ImageIcon,
  label: "Image",
  dimensions: { width: 160, height: 160 },
};

// Components panel items (moved from bottom toolbar)
const componentsItems = [
  {
    type: "true_false",
    icon: ToggleLeft,
    label: "True/False",
    dimensions: { width: 128, height: 56 },
  },
  {
    type: "multiple_choice_single",
    icon: Radio,
    label: "Single Choice",
    dimensions: { width: 288, height: 200 },
  },
  {
    type: "multiple_choice_multi",
    icon: CheckSquare,
    label: "Multiple Choice",
    dimensions: { width: 288, height: 200 },
  },
  {
    type: "single_checkbox",
    icon: Square,
    label: "Checkbox",
    dimensions: { width: 48, height: 48 },
  },
  {
    type: "toggle_button",
    icon: MousePointerClick,
    label: "Toggle",
    dimensions: { width: 100, height: 40 },
  },
  {
    type: "short_text_answer",
    icon: Edit,
    label: "Short Text",
    dimensions: { width: 256, height: 64 },
  },
  {
    type: "numeric_slider",
    icon: Target,
    label: "Number Slider",
    dimensions: { width: 320, height: 160 },
  },
  {
    type: "discrete_slider",
    icon: SlidersHorizontal,
    label: "Label Slider",
    dimensions: { width: 320, height: 160 },
  },
  {
    type: "ranking",
    icon: ListOrdered,
    label: "Ranking",
    dimensions: { width: 320, height: 220 },
  },
  {
    type: "matching_pairs",
    icon: SplitSquareHorizontal,
    label: "Matching",
    dimensions: { width: 400, height: 200 },
  },
];

// Shapes panel items
const shapesItems = [
  {
    type: "shape",
    shapeType: "rectangle",
    icon: Square,
    label: "Rectangle",
    dimensions: { width: 100, height: 100 },
  },
  {
    type: "shape",
    shapeType: "circle",
    icon: Circle,
    label: "Circle",
    dimensions: { width: 100, height: 100 },
  },
  {
    type: "shape",
    shapeType: "triangle",
    icon: Triangle,
    label: "Triangle",
    dimensions: { width: 100, height: 100 },
  },
  {
    type: "shape",
    shapeType: "hexagon",
    icon: Hexagon,
    label: "Hexagon",
    dimensions: { width: 100, height: 100 },
  },
  {
    type: "shape",
    shapeType: "star",
    icon: Star,
    label: "Star",
    dimensions: { width: 100, height: 100 },
  },
  {
    type: "line",
    icon: Spline,
    label: "Line",
    dimensions: { width: 100, height: 2 },
  },
];

const DraggableItem = ({
  type,
  shapeType,
  icon: Icon,
  label,
  dimensions,
  isMobile,
  strokeWidth,
  strokeColor,
  arrowType,
  endpointColor,
  endpointShape,
  showCaption = false,
}) => {
  const [{ isDragging }, drag] = useDrag({
    type: "component",
    item: {
      type,
      shapeType,
      dimensions,
      strokeWidth,
      strokeColor,
      arrowType,
      endpointColor,
      endpointShape,
    },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  });

  return (
    <motion.div
      ref={drag}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      className={`flex ${
        showCaption ? "flex-col" : ""
      } items-center justify-center py-2 rounded-lg hover:bg-gray-50 transition-colors cursor-grab active:cursor-grabbing ${
        isDragging ? "opacity-50" : "opacity-100"
      }`}
      style={{ width: "100%", padding: isMobile ? "0.25rem 0" : "0.5rem 0" }}
      title={label}
      aria-label={label}
    >
      <Icon size={isMobile ? 20 : 24} className="text-gray-700" />
      {showCaption && (
        <span className="text-[10px] text-gray-600 leading-tight mt-0.5 text-center">
          {label}
        </span>
      )}
    </motion.div>
  );
};

const RightToolbar = ({ isOpen = true, onToggle }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [activePanel, setActivePanel] = useState(null); // 'components' | 'shapes' | null
  const containerRef = useRef(null);

  // Isolated scroll
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const stop = (e) => e.stopPropagation();
    container.addEventListener("wheel", stop, { passive: false });
    container.addEventListener("touchmove", stop, { passive: false });
    return () => {
      container.removeEventListener("wheel", stop);
      container.removeEventListener("touchmove", stop);
    };
  }, []);

  // Sync responsive behavior
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const togglePanel = (panel) => {
    setActivePanel((prev) => (prev === panel ? null : panel));
  };

  return (
    <div
      className="relative h-full flex-shrink-0"
      style={{ width: isOpen ? 64 : 0 }}
      aria-hidden={!isOpen}
    >
      {isOpen && (
        <motion.div
          initial={{ x: 40, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ type: "spring", damping: 25, stiffness: 200 }}
          className="h-full"
        >
          <div className="bg-white h-full w-16 border-l border-gray-200 shadow-sm overflow-visible relative">
            {/* Collapse button centered at top */}
            <div className="absolute top-2 left-1/2 -translate-x-1/2 z-20">
              <button
                onClick={() => {
                  setActivePanel(null);
                  onToggle?.();
                }}
                className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 border border-gray-300 shadow-sm text-gray-700"
                aria-label="Collapse right sidebar"
                title="Collapse"
              >
                <PanelRightClose size={18} />
              </button>
            </div>

            {/* Sub-sidebar panels */}
            {activePanel && (
              <div
                className="absolute right-full top-0 bottom-0 border shadow-md z-30"
                style={{
                  width: 72,
                  background:
                    "linear-gradient(180deg, rgba(255,255,255,0.95), rgba(255,255,255,0.85))",
                  borderColor: "rgba(255,255,255,0.6)",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
                }}
              >
                <button
                  onClick={() => setActivePanel(null)}
                  className="absolute top-1 left-1 p-1 rounded hover:bg-gray-100 text-gray-700 z-10"
                  aria-label="Close panel"
                  title="Close"
                >
                  <X size={14} />
                </button>
                <div
                  className="h-full overflow-y-auto px-1 pt-8 pb-12"
                  style={{ scrollbarWidth: "thin" }}
                >
                  <div className="flex flex-col items-center gap-1">
                    {(activePanel === "components"
                      ? componentsItems
                      : shapesItems
                    ).map((item) => (
                      <DraggableItem
                        key={item.type + (item.shapeType || "")}
                        {...item}
                        isMobile={isMobile}
                        showCaption
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Main narrow bar */}
            <div
              ref={containerRef}
              className="h-full overflow-y-auto px-1 pb-10 pt-14"
              style={{
                scrollbarWidth: "thin",
                overscrollBehavior: "contain",
                isolation: "isolate",
              }}
            >
              <div className="flex flex-col items-center gap-2">
                {/* Components trigger */}
                <button
                  onClick={() => togglePanel("components")}
                  className={`w-full flex flex-col items-center justify-center py-2 rounded-lg transition-colors ${
                    activePanel === "components"
                      ? "bg-gray-100"
                      : "hover:bg-gray-50"
                  }`}
                  title="Components"
                  aria-label="Components"
                >
                  <PencilRuler size={20} className="text-gray-700" />
                  <span className="text-[10px] text-gray-600 leading-tight mt-0.5">
                    Tools
                  </span>
                </button>

                {/* Shapes trigger */}
                <button
                  onClick={() => togglePanel("shapes")}
                  className={`w-full flex flex-col items-center justify-center py-2 rounded-lg transition-colors ${
                    activePanel === "shapes"
                      ? "bg-gray-100"
                      : "hover:bg-gray-50"
                  }`}
                  title="Shapes"
                  aria-label="Shapes"
                >
                  <Shapes size={20} className="text-gray-700" />
                  <span className="text-[10px] text-gray-600 leading-tight mt-0.5">
                    Shapes
                  </span>
                </button>

                {/* Standalone Text and Image */}
                <DraggableItem {...textItem} isMobile={isMobile} showCaption />
                <DraggableItem {...imageItem} isMobile={isMobile} showCaption />
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default RightToolbar;
